import { useState, useEffect } from "react";
import { homepageImageListApi, homepageImageCreateApi } from "../../../core/http/httpClient";
import { ToastType } from "../../atoms/Toast";

export const useHomepageImageToast = () => {
  const [toast, setToast] = useState<{
    message: string;
    type: ToastType;
    visible: boolean;
  }>({ message: "", type: "success", visible: false });

  const showToast = (message: string, type: ToastType = "success") => {
    setToast({ message, type, visible: true });
    setTimeout(() => {
      setToast((t) => ({ ...t, visible: false }));
    }, 2000);
  };

  const hideToast = () => setToast((t) => ({ ...t, visible: false }));

  return { toast, showToast, hideToast };
};

export const useHomepageImage = () => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch current homepage cover image on component mount
  useEffect(() => {
    fetchCurrentImage();
  }, []);

  // Update preview when file changes
  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  }, [file]);

  const fetchCurrentImage = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await homepageImageListApi.call({});

      if (response.data) {
        // Handle case when image exists: {"id": 1, "image": "/media/..."}
        if (response.data.image) {
          const imageUrl = buildImageUrl(response.data.image);
          setCurrentImage(imageUrl);
        }
        // Handle case when no image exists: {"cover":null,"message":"No cover image found."}
        else if (response.data.cover === null || response.data.message) {
          // This is a valid "no image" response, not an error
          setCurrentImage(null);
        }
        // Handle unexpected response format
        else {
          console.warn('Unexpected response format:', response.data);
          setCurrentImage(null);
        }
      } else {
        // No response data at all
        setCurrentImage(null);
      }
    } catch (err) {
      console.error('Error fetching homepage image:', err);
      setError('Failed to load current homepage image');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (showToast: (message: string, type?: ToastType) => void) => {
    if (!file) {
      showToast("Please select an image first!", "error");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await homepageImageCreateApi.call({ image: file });

      if (response.data) {
        // Update the current image with the new one
        const imageUrl = buildImageUrl(response.data.image);
        setCurrentImage(imageUrl);
        setPreview(null);
        setFile(null);
        showToast("Homepage image updated successfully!", "success");
      }
    } catch (err) {
      console.error('Error updating homepage image:', err);
      setError('Failed to update homepage image');
      showToast("Failed to update homepage image. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to build image URL that works in both dev and production
  const buildImageUrl = (imagePath: string): string => {
    if (!imagePath) return '';

    // Priority 0: if it's already an absolute URL, return it as is
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }

    // Priority 1: Use dedicated media URL environment variable if available
    if (process.env.REACT_APP_MEDIA_URL) {
      const mediaUrl = process.env.REACT_APP_MEDIA_URL.endsWith('/')
        ? process.env.REACT_APP_MEDIA_URL.slice(0, -1)
        : process.env.REACT_APP_MEDIA_URL;
      return `${mediaUrl}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`;
    }

    // Priority 2: Use API base URL and construct media URL (works in production)
    const apiBaseUrl = process.env.REACT_APP_API_BASE || '';
    if (apiBaseUrl) {
      try {
        const url = new URL(apiBaseUrl);
        const mediaBaseUrl = `${url.protocol}//${url.host}`;
        // Ensure imagePath starts with / for proper URL construction
        const normalizedPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
        return `${mediaBaseUrl}${normalizedPath}`;
      } catch (error) {
        console.warn('Invalid API base URL, falling back to relative path');
      }
    }

    // Priority 3: Fallback to relative path (works if frontend and media are on same domain)
    return imagePath;
  };

  return {
    file,
    setFile,
    preview,
    currentImage,
    loading,
    error,
    handleUpdate,
  };
};